/**
 * StageFooter — the sticky per-stage calculation footer rendered at the bottom
 * of a pipeline column.
 *
 * Purely presentational: it receives already-computed lines from the parent and
 * renders one label/value row per line. Renders `null` (no element at all) when
 * there are no lines, so a stage with no configured footer takes no space.
 *
 * Visual language matches `CardFooter` (PipelineCardParts): a top border with
 * small muted labels and bold right-aligned values.
 */

import { memo } from 'react';

import type { StageFooterLine } from '../utils/computeStageFooter';

export interface StageFooterProps {
  lines: StageFooterLine[];
}

export const StageFooter = memo(function StageFooter({
  lines,
}: StageFooterProps) {
  if (lines.length === 0) {
    return null;
  }

  return (
    // `mx-3 mb-3` insets the footer itself. The pipeline column deliberately
    // has no `p-3` — that gutter moved inside the scroll area so the scrollbar
    // can sit against the column edge — so each child now insets itself.
    //
    // Applied here rather than via a wrapper at the call site on purpose: this
    // component returns `null` when there are no lines, and a wrapping div would
    // leave 24px of phantom margin in every column without a configured footer.
    <div className='flex-shrink-0 mx-3 mb-3 mt-2 pt-2 space-y-1 border-t border-[var(--color-border-secondary)] dark:border-white/15'>
      {lines.map(line => (
        <div
          key={line.key}
          className='flex items-center justify-between gap-2 px-1'
        >
          <span className='text-xs text-[var(--color-text-tertiary)] truncate'>
            {line.label}
          </span>
          <span className='text-xs font-semibold text-[var(--color-text-primary)] flex-shrink-0'>
            {line.value}
          </span>
        </div>
      ))}
    </div>
  );
});
